import {advancedFormArray, advancedFormControl, FormControlTypeEnum,} from '@magieno/common';
import {translationBaseKey} from '../../../decorators/translation-base-key.decorator';
import {FieldTypeEnum} from '../../../enums/field-type.enum';
import {IsBoolean, IsInt, IsNotEmpty, IsString, ValidateIf, ValidateNested} from '@pristine-ts/class-validator';
import {RolloverRequiredInformationPropertiesTransferredOptions,} from './rollover-required-information-properties-transferred.options';

import {ErrorMessageBuilder} from '../../../builders/error-message.builder';
import {CalculationDate} from '../../../models/calculation.date';

@translationBaseKey('rollover-required-information-options')
export class RolloverRequiredInformationOptions {

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de la vente ou du transfert de tous les biens transférés:',
        en: 'Date of sale or transfer of all transferred properties:',
      },
      tooltip: {
        fr: 'La date à laquelle la vente ou le transfert de tous les biens transférés a eu lieu. Cette information est requise pour évaluer les conséquences fiscales de la transaction.',
        en: 'The date on which the sale or transfer of all transferred properties took place. This information is required to assess the tax implications of the transaction.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  sellingDate: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Entente écrite concernant le transfert:',
        en: 'Written agreement concerning the transfer:',
      },
      tooltip: {
        fr: 'Indique si un accord écrit concernant le transfert des biens a été établi. Une entente écrite peut avoir des implications fiscales importantes.',
        en: 'Indicates whether a written agreement concerning the transfer of properties has been established. A written agreement may have significant tax implications.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isWrittenAgreement: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Biens visés par une clause de rajustement du prix:',
        en: 'Properties covered by a price adjustment clause:',
      },
      tooltip: {
        fr: 'Indique si les biens transférés sont visés par une clause de rajustement du prix. Une telle clause peut avoir des effets sur la détermination du gain en capital.',
        en: 'Indicates whether the transferred properties are covered by a price adjustment clause. Such a clause may affect the determination of capital gains.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isPriceAdjustmentClause: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Autres personnes détenant ou contrôlant, directement ou indirectement, des actions d\'une catégorie du capital-actions du cessionnaire:',
        en: 'Other individuals holding or controlling, directly or indirectly, shares of a category of the capital stock of the transferee:',
      },
      tooltip: {
        fr: 'Indique si d\'autres personnes détiennent ou contrôlent, directement ou indirectement, des actions d\'une catégorie du capital-actions du cessionnaire. Cette information peut être pertinente pour les règles fiscales relatives aux opérations entre parties liées.',
        en: 'Indicates whether other individuals hold or control, directly or indirectly, shares of a category of the capital stock of the transferee. This information may be relevant for tax rules concerning transactions between related parties.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isOtherShareholder: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Roulement entre deux ou plusieurs sociétés ayant un lien de dépendance:',
        en: 'Rollover between two or more companies with a dependent relationship:',
      },
      tooltip: {
        fr: 'Indique si un roulement fiscal s\'applique entre deux ou plusieurs sociétés ayant un lien de dépendance. Un roulement fiscal permet de reporter les gains en capital sur les biens transférés.',
        en: 'Indicates whether a tax rollover applies between two or more companies with a dependent relationship. A tax rollover allows deferring capital gains on transferred properties.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isNonArmLengthCorporation: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Biens de la (des) sociétés tous ou presque tous (au moins 90%) transférés à la société cessionnaire:',
        en: 'Property of the company(ies) all or almost all (at least 90%) transferred to the transferee company:',
      },
      tooltip: {
        fr: 'Indique si tous ou presque tous (au moins 90%) des biens de la (des) sociétés ont été transférés à la société cessionnaire. Cette condition est nécessaire pour bénéficier du roulement entre sociétés ayant un lien de dépendance.',
        en: 'Indicates whether all or almost all (at least 90%) of the property of the company(ies) has been transferred to the transferee company. This condition is necessary to qualify for the rollover between companies with a dependent relationship.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isNonArmLengthCorporation)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isTransferToBuyerCorporation: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Contribuable est-il un non-résident du Canada?',
        en: 'Is the taxpayer a non-resident of Canada?',
      },
      tooltip: {
        fr: 'Indique si le contribuable est un non-résident du Canada aux fins fiscales. Les non-résidents sont assujettis à des règles fiscales spécifiques.',
        en: 'Indicates whether the taxpayer is a non-resident of Canada for tax purposes. Non-residents are subject to specific tax rules.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isNonArmLengthCorporation === false)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isNonResident: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Biens transférés comprennent-ils des immobilisations?',
        en: 'Do the transferred properties include capital assets?',
      },
      tooltip: {
        fr: 'Indique si les biens transférés comprennent des immobilisations (biens à long terme). Les immobilisations sont généralement soumises à des règles fiscales spécifiques.',
        en: 'Indicates whether the transferred properties include capital assets (long-term assets). Capital assets are generally subject to specific tax rules.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isCapitalProperty: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Biens détenus sans interruption depuis le jour de l\'évaluation?',
        en: 'Properties held without interruption since the assessment day?',
      },
      tooltip: {
        fr: 'Indique si les biens transférés ont été détenus sans interruption depuis le jour de l\'évaluation. Cette information peut être pertinente pour déterminer si un roulement fiscal s\'applique.',
        en: 'Indicates whether the transferred properties have been held without interruption since the assessment day. This information may be relevant to determine if a tax rollover applies.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isCapitalProperty)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isAssessmentDay: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Acquis après le jour de l\'évaluation dans le cadre d\'une opération comportant un lien de dépendance',
        en: 'Acquired after assessment day in a transaction involving a dependency link',
      },
      tooltip: {
        fr: 'Indique si les biens transférés ont été acquis après le jour de l\'évaluation dans le cadre d\'une opération comportant un lien de dépendance. Cette information peut être importante pour déterminer l\'éligibilité à certains traitements fiscaux.',
        en: 'Indicates whether the transferred properties were acquired after the assessment day in a transaction involving a dependency link. This information may be important to determine eligibility for certain tax treatments.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isCapitalProperty)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isAssessmentDayAndNonArmLength: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Réception de dividendes visés par le paragraphe 83(1) à l\'égard des actions transférées (Si oui, précisez les montants et les dates et annexez un tableau)',
        en: 'Receipt of dividends targeted by paragraph 83(1) with respect to transferred shares (If yes, specify amounts and dates and attach a table)',
      },
      tooltip: {
        fr: 'Indique si le contribuable a reçu des dividendes visés par le paragraphe 83(1) à l\'égard des actions transférées. Cette information est requise pour évaluer les conséquences fiscales des dividendes reçus.',
        en: 'Indicates whether the taxpayer received dividends targeted by paragraph 83(1) with respect to the transferred shares. This information is required to assess the tax implications of the received dividends.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isCapitalProperty)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isAssessmentDayAnd83_1: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant convenu à l\'égard d\'un des biens transférés repose sur une estimation de la JVM au jour de l\'évaluation',
        en: 'Agreed amount for one of the transferred assets is based on an estimate of the FMV on the assessment day',
      },
      tooltip: {
        fr: 'Indique si le montant convenu à l\'égard d\'un des biens transférés repose sur une estimation de la juste valeur marchande (JVM) au jour de l\'évaluation. Cette information peut être importante pour évaluer la précision des montants convenus.',
        en: 'Indicates whether the agreed amount for one of the transferred assets is based on an estimate of the fair market value (FMV) on the assessment day. This information may be important to assess the accuracy of the agreed amounts.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isFairMarketValueOnAssessmentDay: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Rapport officiel faisant état de la valeur au jour de l\'évaluation',
        en: 'Official report stating the value on the assessment day',
      },
      tooltip: {
        fr: 'Indique si un rapport officiel faisant état de la valeur au jour de l\'évaluation a été fourni. Cette information peut être requise pour justifier les montants convenus basés sur une évaluation professionnelle.',
        en: 'Indicates whether an official report stating the value on the assessment day has been provided. This information may be required to justify the agreed amounts based on a professional evaluation.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isFairMarketValueOnAssessmentDay)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isOfficialReport: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Choix selon le paragraphe 26(7) des Règles concernant l\'application de l\'impôt sur le revenu (formulaire T2076) produit par le contribuable ou en son nom',
        en: 'Choice according to paragraph 26(7) of the Rules concerning the application of income tax (form T2076) produced by the taxpayer or on his behalf',
      },
      tooltip: {
        fr: 'Indique si le contribuable a fait le choix selon le paragraphe 26(7) des Règles concernant l\'application de l\'impôt sur le revenu. Ce choix peut avoir des conséquences fiscales spécifiques.',
        en: 'Indicates whether the taxpayer has made the choice according to paragraph 26(7) of the Rules concerning the application of income tax. This choice may have specific tax implications.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isChoice_26_7: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de signature',
        en: 'Signature date',
      },
      tooltip: {
        fr: 'La date à laquelle le formulaire a été signé par le contribuable. Cette date est nécessaire pour l\'authenticité et l\'enregistrement du formulaire.',
        en: 'The date on which the form was signed by the taxpayer. This date is required for the authenticity and registration of the form.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  signatureDate: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant convenu pour un des biens transférés à la colonne E du tableau de la partie 3.1 différent de celui indiqué dans le formulaire fédéral T2057',
        en: 'Agreed amount for one of the assets transferred to column E of the table in part 3.1 differs from that indicated in the federal T2057 form',
      },
      tooltip: {
        fr: 'Indique si le montant convenu pour l\'un des biens transférés à la colonne E du tableau de la partie 3.1 est différent de celui indiqué dans le formulaire fédéral T2057. Cela peut être important pour évaluer les divergences entre les déclarations fiscales fédérales et provinciales.',
        en: 'Indicates whether the agreed amount for one of the assets transferred to column E of the table in part 3.1 differs from that indicated in the federal T2057 form. This may be important to assess discrepancies between federal and provincial tax filings.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isTheAgreedAmountDifferentForQuebecRolloverForm: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Écart entre les sommes convenues au fédéral et au provincial expliqué en totalité ou en quasi-totalité par la différence entre le coût du bien (aux fins de l’application de la législation québécoise) que vous inscrivez à la colonne C de ce tableau et le coût du bien (aux fins de l’application de la législation fédérale) inscrit dans le formulaire fédéral T2057',
        en: 'Gap between the amounts agreed at the federal and provincial levels explained entirely or almost entirely by the difference between the cost of the asset (for the purposes of applying Quebec legislation) that you enter in column C of this table and the cost of the asset (for the purposes of applying federal legislation) entered in the federal T2057 form',
      },
      tooltip: {
        fr: 'Indique si l\'écart entre les montants convenus au fédéral et au provincial est expliqué en totalité ou en quasi-totalité par la différence entre le coût du bien (aux fins de l\'application de la législation québécoise) que vous inscrivez à la colonne C du tableau et le coût du bien (aux fins de l\'application de la législation fédérale) inscrit dans le formulaire fédéral T2057. Cette information peut être nécessaire pour justifier les différences entre les deux déclarations fiscales.',
        en: 'Indicates whether the gap between the amounts agreed at the federal and provincial levels is explained entirely or almost entirely by the difference between the cost of the asset (for the purposes of applying Quebec legislation) that you enter in column C of the table and the cost of the asset (for the purposes of applying federal legislation) entered in the federal T2057 form. This information may be required to justify the differences between the two tax filings.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isTheAgreedAmountDifferentForQuebecRolloverForm)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isTheAgreedAmountForQuebecRolloverFormDifferentBecauseOfCost: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Raison de cet écart (si vous avez répondu non):',
        en: 'Reason for this discrepancy (if you answered no):',
      },
      tooltip: {
        fr: 'Si vous avez répondu non à la question précédente, indiquez la raison de l\'écart entre les montants convenus au fédéral et au provincial. Cette information peut aider à expliquer les différences entre les deux déclarations fiscales.',
        en: 'If you answered no to the previous question, specify the reason for the gap between the amounts agreed at the federal and provincial levels. This information may help explain the differences between the two tax filings.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.isTheAgreedAmountDifferentForQuebecRolloverForm &&
    o.isTheAgreedAmountForQuebecRolloverFormDifferentBecauseOfCost === false,
  )
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  reasonForDifferentAgreedAmountForQuebecRolloverForm: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Choix en vertu du paragraphe 85(1) de la LIR pour les biens décrits à la partie 3',
        en: 'Choice under subsection 85(1) of the ITA for property described in Part 3',
      },
      tooltip: {
        fr: 'Indique si le choix a été fait en vertu du paragraphe 85(1) de la Loi de l\'impôt sur le revenu (LIR) pour les biens décrits à la partie 3. Ce choix peut avoir des conséquences fiscales spécifiques.',
        en: 'Indicates whether the choice has been made under subsection 85(1) of the Income Tax Act (ITA) for property described in Part 3. This choice may have specific tax implications.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isThe85LIRChoiceBeenMade: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Demande d’agrément en vertu du troisième alinéa de l’article 522 de la Loi sur les impôts pour les biens décrits à la partie 3',
        en: 'Application for approval under the third paragraph of section 522 of the Income Tax Act for property described in Part 3',
      },
      tooltip: {
        fr: 'Indique si une demande d\'agrément a été faite en vertu du troisième alinéa de l\'article 522 de la Loi sur les impôts pour les biens décrits à la partie 3. Cette demande peut être nécessaire pour certains types de transactions.',
        en: 'Indicates whether an application for approval has been made under the third paragraph of section 522 of the Income Tax Act for property described in Part 3. This application may be required for certain types of transactions.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isThirdParagraphOfSection522ChoiceBeenMade: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Aucun choix ne peut être fait en vertu du paragraphe 85(1) de la LIR en raison de l’application du paragraphe 13(21.2) de cette loi et les parties désirent présenter une demande de roulement propre à la fiscalité québécoise?',
        en: 'No choice can be made under subsection 85(1) of the ITA due to the application of subsection 13(21.2) of this Act, and the parties wish to file a Quebec tax rollover request?',
      },
      tooltip: {
        fr: 'Indique si aucun choix ne peut être fait en vertu du paragraphe 85(1) de la Loi de l\'impôt sur le revenu (LIR) en raison de l\'application du paragraphe 13(21.2) de cette loi, et que les parties souhaitent présenter une demande de roulement propre à la fiscalité québécoise. Cette demande peut être nécessaire dans certains scénarios fiscaux.',
        en: 'Indicates whether no choice can be made under subsection 85(1) of the Income Tax Act (ITA) due to the application of subsection 13(21.2) of this Act, and the parties wish to file a Quebec tax rollover request. This request may be required in certain tax scenarios.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  is13_21_2ApplicableForQuebecRolloverForm: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Lien de dépendance entre les parties?',
        en: 'Relationship of dependence between the parties?',
      },
      tooltip: {
        fr: 'Indique si un lien de dépendance existe entre les parties. Cette information peut être pertinente pour déterminer l\'éligibilité à certaines dispositions fiscales.',
        en: 'Indicates whether a relationship of dependence exists between the parties. This information may be relevant in determining eligibility for certain tax provisions.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.is13_21_2ApplicableForQuebecRolloverForm)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  nonArmLengthForQuebecRolloverForm: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Biens du cédant tous ou presque tous (au moins 90 %) transférés à la société cessionnaire?',
        en: 'Property of the transferor all or almost all (at least 90%) transferred to the acquiring corporation?',
      },
      tooltip: {
        fr: 'Indique si tous ou presque tous (au moins 90 %) des biens du cédant ont été transférés à la société cessionnaire. Cette information peut être pertinente pour déterminer l\'admissibilité à certaines dispositions fiscales.',
        en: 'Indicates whether all or almost all (at least 90%) of the property of the transferor has been transferred to the acquiring corporation. This information may be relevant in determining eligibility for certain tax provisions.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @ValidateIf(o => o.is13_21_2ApplicableForQuebecRolloverForm)
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  transferToBuyerCorporationForQuebecRolloverForm: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de la catégorie:',
        en: 'Category number:',
      },
      tooltip: {
        fr: 'Indique le numéro de la catégorie pour la dépréciation (CCA) associée à cette information. Cela peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the category number for the Capital Cost Allowance (CCA) associated with this information. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  depreciationCategory44: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'PNACC de la catégorie avant le transfert:',
        en: 'CCA Pool of the category before the transfer:',
      },
      tooltip: {
        fr: 'Indique la valeur du Pool de Nouvelles Acquisitions en Capital Canadien (PNACC) pour la catégorie de dépréciation avant le transfert. Cette information peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the value of the Canadian Class Capital Addition (CCA) Pool for the depreciation category before the transfer. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  uCCBeforeTransfer44: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de la catégorie:',
        en: 'Category number:',
      },
      tooltip: {
        fr: 'Indique le numéro de la catégorie pour la dépréciation (CCA) associée à cette information. Cela peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the category number for the Capital Cost Allowance (CCA) associated with this information. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  depreciationCategory45: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'PNACC de la catégorie avant le transfert:',
        en: 'CCA Pool of the category before the transfer:',
      },
      tooltip: {
        fr: 'Indique la valeur du Pool de Nouvelles Acquisitions en Capital Canadien (PNACC) pour la catégorie de dépréciation avant le transfert. Cette information peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the value of the Canadian Class Capital Addition (CCA) Pool for the depreciation category before the transfer. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  uCCBeforeTransfer45: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de la catégorie:',
        en: 'Category number:',
      },
      tooltip: {
        fr: 'Indique le numéro de la catégorie pour la dépréciation (CCA) associée à cette information. Cela peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the category number for the Capital Cost Allowance (CCA) associated with this information. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  depreciationCategory46: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'PNACC de la catégorie avant le transfert:',
        en: 'CCA Pool of the category before the transfer:',
      },
      tooltip: {
        fr: 'Indique la valeur du Pool de Nouvelles Acquisitions en Capital Canadien (PNACC) pour la catégorie de dépréciation avant le transfert. Cette information peut être nécessaire pour le calcul des ajustements fiscaux.',
        en: 'Indicates the value of the Canadian Class Capital Addition (CCA) Pool for the depreciation category before the transfer. This may be required for tax adjustment calculations.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  uCCBeforeTransfer46: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de la catégorie:',
        en: 'Category number:',
      },
      tooltip: {
        fr: 'Le numéro de la catégorie associée à la dépréciation du bien transféré. Ce champ indique le numéro de la catégorie pour le bien spécifique.',
        en: 'The category number associated with the depreciation of the transferred property. This field indicates the category number for the specific property.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  depreciationCategory47: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'PNACC de la catégorie avant le transfert:',
        en: 'CCA Pool of the category before the transfer:',
      },
      tooltip: {
        fr: 'Le montant du Pool de Crédit d\'Impôt pour Amortissement (PNACC) de la catégorie avant le transfert du bien. Ce montant indique la valeur du PNACC avant le transfert.',
        en: 'The amount of the Capital Cost Allowance (CCA) Pool of the category before the transfer of the property. This amount indicates the value of the CCA Pool before the transfer.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  uCCBeforeTransfer47: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Numéro de la catégorie:',
        en: 'Category number:',
      },
      tooltip: {
        fr: 'Le numéro de la catégorie associée à la dépréciation du bien transféré. Ce champ indique le numéro de la catégorie pour le bien spécifique.',
        en: 'The category number associated with the depreciation of the transferred property. This field indicates the category number for the specific property.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  depreciationCategory48: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'PNACC de la catégorie avant le transfert:',
        en: 'CCA Pool of the category before the transfer:',
      },
      tooltip: {
        fr: 'Le montant du Pool de Crédit d\'Impôt pour Amortissement (PNACC) de la catégorie avant le transfert du bien. Ce montant indique la valeur du PNACC avant le transfert.',
        en: 'The amount of the Capital Cost Allowance (CCA) Pool of the category before the transfer of the property. This amount indicates the value of the CCA Pool before the transfer.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  uCCBeforeTransfer48: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom du cédant ou de la personne autorisée à signer pour le cédant (en majuscules):',
        en: 'Name of the transferor or authorized signatory for the transferor (in capital letters):',
      },
      tooltip: {
        fr: 'Le nom du cédant ou de la personne autorisée à signer pour le cédant. Ce champ doit être rempli en majuscules.',
        en: 'The name of the transferor or authorized signatory for the transferor. This field should be filled in capital letters.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  nameOfTransferOrAuthorizedSignee: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de la personne autorisée à signer pour la société cessionnaire (en majuscules):',
        en: 'Name of the authorized signatory for the acquiring corporation (in capital letters):',
      },
      tooltip: {
        fr: 'Le nom de la personne autorisée à signer pour la société cessionnaire. Ce champ doit être rempli en majuscules.',
        en: 'The name of the authorized signatory for the acquiring corporation. This field should be filled in capital letters.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  nameOfTransferOrAuthorizedSigneeForTheTransferee: string;

  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 1,
    elementClassType: RolloverRequiredInformationPropertiesTransferredOptions,
  })
  @ValidateNested()
  propertiesTransferred: RolloverRequiredInformationPropertiesTransferredOptions[] = [];

}
